/**
 * "This has scrolled into view" — the website's own action, copied across.
 *
 * `../unomena-website/src/lib/actions/inview.ts` is the original; this is the
 * same forty lines, because the reveal choreography on the product page has to
 * be the site's, not a lookalike. Two deliberate differences:
 *
 *  - It is bundled module JS, not an inline script. unomena.com sets `html.js`
 *    from an inline `<script>` in app.html and gates every hidden state on it;
 *    our CSP has no `script-src 'unsafe-inline'` and is not to gain one
 *    (`packages/server/src/http/routes.ts`). There is no gate here at all: this
 *    is a single-page app, so the markup only exists once JS has run, and an
 *    element that is hidden before JS is an element that does not exist yet.
 *
 *  - The hidden state lives in `Login.svelte`'s own scoped `<style>`, under
 *    `@media (prefers-reduced-motion: no-preference)`. Nothing else in the app
 *    reveals on scroll, and a global `[data-reveal]` rule would be a trap for
 *    the next route that happens to use the attribute.
 *
 * No IntersectionObserver (or a test environment without one): reveal
 * immediately. Content is never withheld because an observer is missing.
 */

interface InviewOptions {
  once?: boolean
  rootMargin?: string
}

export function inview(node: HTMLElement, options?: InviewOptions) {
  const { once = true, rootMargin = '-10% 0px' } = options ?? {}

  if (typeof IntersectionObserver === 'undefined') {
    node.classList.add('is-inview')
    return
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          node.classList.add('is-inview')
          if (once) observer.unobserve(node)
        }
      }
    },
    { rootMargin },
  )

  observer.observe(node)

  return {
    destroy() {
      observer.disconnect()
    },
  }
}
